import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowRight,
  Bold,
  CircleCheck,
  Heading1,
  Heading2,
  IndentIncrease,
  Italic,
  LoaderCircle,
  RefreshCw,
  TriangleAlert,
  WandSparkles,
} from "lucide-react";
import { Extension } from "@tiptap/core";
import { TextAlign } from "@tiptap/extension-text-align";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  generatedParagraphAttributes,
  generatedTextNodes,
  normalizeDocument,
  plainTextToDocument,
} from "./document.js";
import {
  chatPromptForAiEdit,
  consumeAiCommand,
} from "./ai-command.js";

const ghostSuggestionKey = new PluginKey("tribunaleaInlineSuggestion");
const emptyGhostSuggestion = { text: "", position: null };
const aiEditTargetKey = new PluginKey("tribunaleaAiEditTarget");
const emptyAiEditTarget = { from: null, to: null };
const maximumAiInstructionHeight = 128;

function resizeAiInstructionTextarea(textarea) {
  if (!textarea) return;
  textarea.style.height = "0px";
  const nextHeight = Math.min(
    textarea.scrollHeight,
    maximumAiInstructionHeight,
  );
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY =
    textarea.scrollHeight > maximumAiInstructionHeight ? "auto" : "hidden";
}

const InlineSuggestion = Extension.create({
  name: "tribunaleaInlineSuggestion",
  addProseMirrorPlugins() {
    return [new Plugin({
      key: ghostSuggestionKey,
      state: {
        init: () => emptyGhostSuggestion,
        apply(transaction, current) {
          const next = transaction.getMeta(ghostSuggestionKey);
          if (next !== undefined) return next;
          if (transaction.docChanged) return emptyGhostSuggestion;
          return current;
        },
      },
      props: {
        decorations(state) {
          const ghost = ghostSuggestionKey.getState(state);
          if (!ghost?.text || !Number.isFinite(ghost.position)) return null;
          const position = Math.max(0, Math.min(ghost.position, state.doc.content.size));
          const decoration = Decoration.widget(position, () => {
            const wrapper = document.createElement("span");
            wrapper.className = "tribunalea-document-ghost";
            wrapper.setAttribute("aria-hidden", "true");
            wrapper.append(document.createTextNode(ghost.text));
            const hint = document.createElement("span");
            hint.className = "tribunalea-document-ghost-hint";
            hint.textContent = "Tab";
            wrapper.append(hint);
            return wrapper;
          }, { side: 1 });
          return DecorationSet.create(state.doc, [decoration]);
        },
      },
    })];
  },
});

const AiEditTarget = Extension.create({
  name: "tribunaleaAiEditTarget",
  addProseMirrorPlugins() {
    return [new Plugin({
      key: aiEditTargetKey,
      state: {
        init: () => emptyAiEditTarget,
        apply(transaction, current) {
          const next = transaction.getMeta(aiEditTargetKey);
          if (next !== undefined) return next;
          if (!Number.isFinite(current?.from) || !transaction.docChanged) {
            return current;
          }
          return {
            from: transaction.mapping.map(current.from, -1),
            to: transaction.mapping.map(current.to, 1),
          };
        },
      },
      props: {
        decorations(state) {
          const target = aiEditTargetKey.getState(state);
          if (!Number.isFinite(target?.from) || !Number.isFinite(target?.to)) {
            return null;
          }
          const from = Math.max(0, Math.min(target.from, state.doc.content.size));
          const to = Math.max(from, Math.min(target.to, state.doc.content.size));
          if (from === to) {
            return DecorationSet.create(state.doc, [
              Decoration.widget(from, () => {
                const cursor = document.createElement("span");
                cursor.className = "tribunalea-document-ai-cursor";
                cursor.setAttribute("aria-hidden", "true");
                return cursor;
              }, { side: 1 }),
            ]);
          }
          return DecorationSet.create(state.doc, [
            Decoration.inline(from, to, {
              class: "tribunalea-document-ai-selection",
            }),
          ]);
        },
      },
    })];
  },
});

const LegalIndent = Extension.create({
  name: "tribunaleaLegalIndent",
  addGlobalAttributes() {
    return [{
      types: ["paragraph"],
      attributes: {
        legalIndent: {
          default: true,
          parseHTML: (element) =>
            element.getAttribute("data-legal-indent") !== "false",
          renderHTML: ({ legalIndent }) => ({
            "data-legal-indent": legalIndent ? "true" : "false",
          }),
        },
      },
    }];
  },
});

function showInlineSuggestion(editor, text, position) {
  if (!editor) return;
  editor.view.dispatch(
    editor.state.tr.setMeta(ghostSuggestionKey, { text, position }),
  );
}

function showAiEditTarget(editor, selection) {
  if (!editor) return;
  editor.view.dispatch(
    editor.state.tr.setMeta(aiEditTargetKey, selection || emptyAiEditTarget),
  );
}

function suggestionWithSpacing(editor, suggestion, position) {
  const before = position > 0
    ? editor.state.doc.textBetween(position - 1, position, "", "")
    : "";
  const needsSpace = before && !/\s/.test(before) && !/^[,.;:!?)]/.test(suggestion);
  return `${needsSpace ? " " : ""}${suggestion}`;
}

function documentFingerprint(editor) {
  return JSON.stringify(editor.getJSON());
}

function generatedTextContent(value) {
  const text = String(value || "").replace(/\r\n?/g, "\n");
  if (!text.includes("\n")) return { type: "text", text };
  return generatedTextNodes(text);
}

function paragraphHasTextAt(editor, position) {
  const resolved = editor.state.doc.resolve(position);
  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    const node = resolved.node(depth);
    if (node.type.name === "paragraph") return Boolean(node.textContent.trim());
  }
  return false;
}

function applyGeneratedParagraphFormatting(editor, chain, text, position) {
  const attributes = generatedParagraphAttributes(text);
  if (paragraphHasTextAt(editor, position)) delete attributes.textAlign;
  return chain.updateAttributes(
    "paragraph",
    attributes,
  );
}

function findTextRange(editor, targetText) {
  const target = String(targetText || "");
  if (!editor || !target) return null;
  const segments = [];
  editor.state.doc.descendants((node, position) => {
    if (node.isText && node.text) {
      segments.push({ position, text: node.text });
    }
  });
  let searchable = "";
  const positions = [];
  let previousEnd = null;
  segments.forEach((segment) => {
    if (searchable && segment.position !== previousEnd) {
      searchable += "\n";
      positions.push(null);
    }
    for (let index = 0; index < segment.text.length; index += 1) {
      searchable += segment.text[index];
      positions.push(segment.position + index);
    }
    previousEnd = segment.position + segment.text.length;
  });
  const index = searchable.indexOf(target);
  if (index < 0) return null;
  const mapped = positions.slice(index, index + target.length);
  if (!mapped.length || mapped.some((position) => position === null)) return null;
  return { from: mapped[0], to: mapped.at(-1) + 1 };
}

function ToolbarButton({ active = false, children, label, onClick }) {
  return (
    <button
      aria-label={label}
      aria-pressed={active}
      className={`tribunalea-document-tool ${active ? "is-active" : ""}`}
      onClick={onClick}
      title={label}
      type="button"
    >
      {children}
    </button>
  );
}

function contextFailureLabel(error) {
  const normalized = String(error || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (normalized.includes("credito")) return "Créditos insuficientes";
  return "Contexto no disponible";
}

export function DocumentEditor({
  value,
  onChange,
  onRequestSuggestion = null,
  onRequestAiEdit = null,
  onAiEditApplied = null,
  aiCommand = null,
  onAiCommandHandled = null,
  onRouteToChat = null,
  placeholder = "Empezá a escribir…",
  readOnly = false,
  autoSuggestDefault = false,
  contextLabel = "Contexto disponible",
  documentLabel = "escrito",
  suggestionContextStatus = "ready",
  suggestionContextError = "",
  onRetrySuggestionContext = null,
  toolbarActions = null,
  documentFooter = null,
}) {
  const [suggestion, setSuggestion] = useState("");
  const [suggestionPosition, setSuggestionPosition] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionError, setSuggestionError] = useState("");
  const [autoSuggest, setAutoSuggest] = useState(autoSuggestDefault);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiEditLoading, setAiEditLoading] = useState(false);
  const [aiEditError, setAiEditError] = useState("");
  const [aiEditVariants, setAiEditVariants] = useState([]);
  const [aiCommandActive, setAiCommandActive] = useState(false);
  const suggestionsEnabled = Boolean(onRequestSuggestion);
  const aiToolsEnabled = suggestionsEnabled || Boolean(onRequestAiEdit);
  const suggestionContextReady = suggestionContextStatus === "ready";
  const requestSequence = useRef(0);
  const requestInFlight = useRef(false);
  const suggestionRequester = useRef(onRequestSuggestion);
  const aiEditRequester = useRef(onRequestAiEdit);
  const aiEditSequence = useRef(0);
  const aiEditSelection = useRef(null);
  const aiEditComparison = useRef(null);
  const handledAiCommand = useRef(null);
  const aiInstructionTextarea = useRef(null);
  const lastRequestedDocument = useRef("");
  const serializedValue = useMemo(
    () => JSON.stringify(normalizeDocument(value)),
    [value],
  );

  useEffect(() => {
    suggestionRequester.current = onRequestSuggestion;
  }, [onRequestSuggestion]);

  useEffect(() => {
    aiEditRequester.current = onRequestAiEdit;
  }, [onRequestAiEdit]);

  useEffect(() => {
    resizeAiInstructionTextarea(aiInstructionTextarea.current);
  }, [aiInstruction]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        orderedList: false,
        strike: false,
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      LegalIndent,
      InlineSuggestion,
      AiEditTarget,
    ],
    content: normalizeDocument(value),
    editable: !readOnly,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "tribunalea-document-content",
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor: activeEditor }) => {
      requestSequence.current += 1;
      requestInFlight.current = false;
      setSuggestionLoading(false);
      setSuggestion("");
      setSuggestionPosition(null);
      setSuggestionError("");
      setAiEditVariants([]);
      aiEditComparison.current = null;
      onChange?.(activeEditor.getJSON());
    },
    onFocus: ({ editor: activeEditor }) => {
      aiEditSelection.current = null;
      setAiCommandActive(false);
      showAiEditTarget(activeEditor, null);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = JSON.stringify(editor.getJSON());
    if (current !== serializedValue) {
      requestSequence.current += 1;
      aiEditSequence.current += 1;
      requestInFlight.current = false;
      lastRequestedDocument.current = "";
      setSuggestionLoading(false);
      setAiEditLoading(false);
      setAiEditVariants([]);
      setSuggestion("");
      setSuggestionPosition(null);
      setAiEditError("");
      setAiCommandActive(false);
      aiEditSelection.current = null;
      aiEditComparison.current = null;
      showAiEditTarget(editor, null);
      editor.commands.setContent(JSON.parse(serializedValue), {
        emitUpdate: false,
      });
    }
  }, [editor, serializedValue]);

  useEffect(() => {
    showInlineSuggestion(editor, suggestion, suggestionPosition);
  }, [editor, suggestion, suggestionPosition]);

  const requestSuggestion = useCallback(async () => {
    const requester = suggestionRequester.current;
    if (!editor || !requester || requestInFlight.current) return;
    if (!suggestionContextReady) {
      setSuggestionError(
        suggestionContextError
        || "Esperá a que termine de prepararse el contexto del expediente.",
      );
      return;
    }
    const text = editor.getText({ blockSeparator: "\n\n" }).trim();
    if (text.length < 20) {
      setSuggestionError("Escribí al menos una oración para recibir una sugerencia.");
      return;
    }
    const document = editor.getJSON();
    const fingerprint = JSON.stringify(document);
    const cursorOffset = editor.state.selection.from;
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    requestInFlight.current = true;
    lastRequestedDocument.current = fingerprint;
    setSuggestionLoading(true);
    setSuggestionError("");
    setSuggestion("");
    setSuggestionPosition(null);
    try {
      const result = await requester({
        document,
        plainText: text,
        cursorOffset,
      });
      if (
        requestSequence.current !== sequence
        || documentFingerprint(editor) !== fingerprint
      ) return;
      const nextSuggestion = String(result?.suggestion || "")
        .replace(/\s+/g, " ")
        .trim();
      if (!nextSuggestion) {
        setSuggestionError(result?.message || "No encontramos una continuación útil.");
        return;
      }
      setSuggestion(suggestionWithSpacing(editor, nextSuggestion, cursorOffset));
      setSuggestionPosition(cursorOffset);
    } catch (error) {
      if (requestSequence.current === sequence) {
        setSuggestionError(error.message || "No pudimos generar la sugerencia.");
      }
    } finally {
      if (requestSequence.current === sequence) {
        requestInFlight.current = false;
        setSuggestionLoading(false);
      }
    }
  }, [editor, suggestionContextError, suggestionContextReady]);

  useEffect(() => {
    if (
      !autoSuggest
      || aiCommandActive
      || !editor
      || !suggestionsEnabled
      || !suggestionContextReady
      || readOnly
    ) return undefined;
    if (suggestion || suggestionLoading) return undefined;
    const text = editor.getText({ blockSeparator: "\n\n" }).trim();
    const fingerprint = documentFingerprint(editor);
    if (
      text.length < 80
      || !editor.state.selection.empty
      || !/[.!?:;]$/.test(text)
      || fingerprint === lastRequestedDocument.current
    ) {
      return undefined;
    }
    const timeout = window.setTimeout(() => requestSuggestion(), 2500);
    return () => window.clearTimeout(timeout);
  }, [
    autoSuggest,
    aiCommandActive,
    editor,
    suggestionsEnabled,
    suggestionContextReady,
    readOnly,
    requestSuggestion,
    serializedValue,
    suggestion,
    suggestionLoading,
  ]);

  const acceptSuggestion = useCallback(() => {
    if (!editor || !suggestion) return;
    const position = Number.isFinite(suggestionPosition)
      ? suggestionPosition
      : editor.state.selection.from;
    applyGeneratedParagraphFormatting(
      editor,
      editor.chain().focus().setTextSelection(position),
      suggestion,
      position,
    ).insertContentAt(position, {
      type: "text",
      text: suggestion,
    }).run();
    // Aceptar una sugerencia es una edición programática: no debe disparar
    // inmediatamente otra solicitud hasta que la persona vuelva a escribir.
    lastRequestedDocument.current = documentFingerprint(editor);
    setSuggestion("");
    setSuggestionPosition(null);
  }, [editor, suggestion, suggestionPosition]);

  const rememberAiEditSelection = useCallback(() => {
    if (!editor) return;
    const selection = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
    aiEditSelection.current = selection;
    setAiCommandActive(true);
    setSuggestion("");
    setSuggestionPosition(null);
    showAiEditTarget(editor, selection);
  }, [editor]);

  const applyAiEditResult = useCallback((
    result,
    { selection, fingerprint, instruction = "" },
  ) => {
    if (!editor) return false;
    const action = result?.action || "insert";
    if (action === "open_chat") {
      const chatPrompt = chatPromptForAiEdit({ instruction, result });
      if (!chatPrompt || !onRouteToChat) {
        setAiEditError("No pudimos trasladar la consulta al chat.");
        return false;
      }
      aiEditSelection.current = null;
      aiEditComparison.current = null;
      setAiEditVariants([]);
      setAiInstruction("");
      setAiCommandActive(false);
      showAiEditTarget(editor, null);
      onRouteToChat(chatPrompt);
      return true;
    }
    if (documentFingerprint(editor) !== fingerprint) {
      setAiEditError(
        `El ${documentLabel} cambió mientras la IA trabajaba. Volvé a enviar la instrucción.`,
      );
      return false;
    }
    let text = String(result?.text || "").trim();
    if (!text) {
      setAiEditError("La IA no devolvió texto para aplicar.");
      return false;
    }
    if (action === "replace_document") {
      editor.commands.setContent(plainTextToDocument(text));
      lastRequestedDocument.current = documentFingerprint(editor);
      aiEditSelection.current = null;
      aiEditComparison.current = null;
      setAiEditVariants([]);
      setAiInstruction("");
      setAiCommandActive(false);
      showAiEditTarget(editor, null);
      onAiEditApplied?.(result);
      return true;
    }
    let range = selection.from;
    if (action === "replace_selection" && selection.to > selection.from) {
      range = { from: selection.from, to: selection.to };
    } else if (action === "replace_text") {
      range = findTextRange(editor, result?.targetText);
      if (!range) {
        setAiEditError(
          `La IA propuso modificar un fragmento que ya no está en el ${documentLabel}.`,
        );
        return false;
      }
    }
    if (action === "insert" && !text.includes("\n")) {
      text = suggestionWithSpacing(editor, text, selection.from);
    }
    const formattingPosition = typeof range === "number" ? range : range.from;
    applyGeneratedParagraphFormatting(
      editor,
      editor.chain().focus().setTextSelection(formattingPosition),
      text,
      formattingPosition,
    ).insertContentAt(range, generatedTextContent(text)).run();
    lastRequestedDocument.current = documentFingerprint(editor);
    aiEditSelection.current = null;
    aiEditComparison.current = null;
    setAiEditVariants([]);
    setAiInstruction("");
    setAiCommandActive(false);
    showAiEditTarget(editor, null);
    onAiEditApplied?.(result);
    return true;
  }, [documentLabel, editor, onAiEditApplied, onRouteToChat]);

  const dismissAiEditComparison = useCallback(() => {
    aiEditComparison.current = null;
    setAiEditVariants([]);
    setAiCommandActive(false);
    showAiEditTarget(editor, null);
  }, [editor]);

  const requestAiEdit = useCallback(async (event, instructionOverride = null) => {
    event?.preventDefault?.();
    const requester = aiEditRequester.current;
    const instruction = String(instructionOverride ?? aiInstruction).trim();
    if (!editor || !requester || aiEditLoading || !instruction) return;
    if (!suggestionContextReady) {
      setAiEditError(
        suggestionContextError
        || "Esperá a que termine de prepararse el contexto del expediente.",
      );
      return;
    }
    const selection = aiEditSelection.current || {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
    const selectedText = selection.to > selection.from
      ? editor.state.doc.textBetween(selection.from, selection.to, "\n", "\n")
      : "";
    const fingerprint = documentFingerprint(editor);
    const sequence = aiEditSequence.current + 1;
    aiEditSequence.current = sequence;
    setAiEditLoading(true);
    setAiEditError("");
    setSuggestion("");
    setSuggestionPosition(null);
    try {
      const result = await requester({
        instruction,
        plainText: editor.getText({ blockSeparator: "\n\n" }).trim(),
        cursorOffset: selection.from,
        selectionFrom: selection.from,
        selectionTo: selection.to,
        selectedText,
      });
      if (aiEditSequence.current !== sequence) return;
      if (result?.variants?.length) {
        aiEditComparison.current = { selection, fingerprint, instruction };
        setAiEditVariants(result.variants);
        return;
      }
      applyAiEditResult(result, { selection, fingerprint, instruction });
    } catch (error) {
      if (aiEditSequence.current === sequence) {
        setAiEditError(error.message || "No pudimos aplicar la instrucción.");
      }
    } finally {
      if (aiEditSequence.current === sequence) setAiEditLoading(false);
    }
  }, [
    aiEditLoading,
    aiInstruction,
    applyAiEditResult,
    editor,
    suggestionContextError,
    suggestionContextReady,
  ]);

  useEffect(() => {
    if (!editor) return undefined;
    consumeAiCommand({
      command: aiCommand,
      handledCommand: handledAiCommand,
      onPrepare: (instruction) => {
        setAiInstruction(instruction);
        setAiEditError("");
        setAiCommandActive(true);
      },
      // La llamada es deliberadamente directa: un timer podía ser cancelado
      // por el render que produce la precarga de la instrucción.
      onSubmit: (instruction) => void requestAiEdit(null, instruction),
      onHandled: onAiCommandHandled,
    });
    return undefined;
  }, [
    aiCommand,
    editor,
    onAiCommandHandled,
    requestAiEdit,
  ]);

  if (!editor) return <div className="tribunalea-document-loading">Preparando editor…</div>;

  return (
    <section
      className="tribunalea-document-editor"
      onKeyDown={(event) => {
        if (event.key === "Tab" && suggestion) {
          event.preventDefault();
          acceptSuggestion();
        }
        if (event.key === "Escape" && suggestion) {
          event.preventDefault();
          setSuggestion("");
        }
      }}
    >
      {!readOnly && (
        <div className="tribunalea-document-toolbar" aria-label={`Herramientas del ${documentLabel}`}>
          <ToolbarButton
            active={editor.isActive("heading", { level: 1 })}
            label="Título"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <Heading1 size={17} />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("heading", { level: 2 })}
            label="Subtítulo"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 size={17} />
          </ToolbarButton>
          <span className="tribunalea-document-toolbar-divider" />
          <ToolbarButton
            active={editor.isActive("bold")}
            label="Negrita"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold size={17} />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("italic")}
            label="Itálica"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic size={17} />
          </ToolbarButton>
          <span className="tribunalea-document-toolbar-divider" />
          <ToolbarButton
            active={editor.isActive({ textAlign: "left" })}
            label="Alinear a la izquierda"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <AlignLeft size={17} />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive({ textAlign: "center" })}
            label="Centrar"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <AlignCenter size={17} />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive({ textAlign: "right" })}
            label="Alinear a la derecha"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <AlignRight size={17} />
          </ToolbarButton>
          <span className="tribunalea-document-toolbar-divider" />
          <ToolbarButton
            active={editor.isActive("paragraph", { legalIndent: true })}
            label="Sangría legal"
            onClick={() => editor.chain().focus().updateAttributes(
              "paragraph",
              {
                legalIndent: !editor.isActive(
                  "paragraph",
                  { legalIndent: true },
                ),
              },
            ).run()}
          >
            <IndentIncrease size={17} />
          </ToolbarButton>
          {toolbarActions && (
            <>
              <span className="tribunalea-document-toolbar-divider" />
              <div className="tribunalea-document-toolbar-actions">
                {toolbarActions}
              </div>
            </>
          )}
          {aiToolsEnabled && (
            <div className="tribunalea-document-ai-tools">
              <span
                aria-live="polite"
                className={`tribunalea-document-context-status is-${suggestionContextStatus}`}
                title={suggestionContextError || undefined}
              >
                {suggestionContextStatus === "processing" && (
                  <>
                    <LoaderCircle className="tribunalea-document-spin" size={14} />
                    Preparando contexto…
                  </>
                )}
                {suggestionContextStatus === "ready" && (
                  <>
                    <CircleCheck size={14} />
                    Contexto listo
                  </>
                )}
                {suggestionContextStatus === "failed" && (
                  <>
                    <TriangleAlert size={14} />
                    {contextFailureLabel(suggestionContextError)}
                    {onRetrySuggestionContext && (
                      <button
                        aria-label="Reintentar preparación del contexto"
                        onClick={onRetrySuggestionContext}
                        title="Reintentar"
                        type="button"
                      >
                        <RefreshCw size={13} />
                      </button>
                    )}
                  </>
                )}
              </span>
              {onRequestSuggestion && (
                <label title="Genera sugerencias luego de una pausa al escribir">
                  <input
                    checked={autoSuggest}
                    disabled={!suggestionContextReady}
                    onChange={(event) => setAutoSuggest(event.target.checked)}
                    type="checkbox"
                  />
                  {suggestionLoading ? "Autocompletando…" : "Autocompletar"}
                </label>
              )}
            </div>
          )}
        </div>
      )}
      {suggestionError && (
        <p className="tribunalea-document-suggestion-error" role="status">
          {suggestionError}
        </p>
      )}
      {!readOnly && onRequestAiEdit && (
        <div className="tribunalea-document-ai-command-shell">
          <form
            className={`tribunalea-document-ai-command ${
              aiEditLoading ? "is-loading" : ""
            }`}
            onSubmit={requestAiEdit}
          >
            <WandSparkles aria-hidden="true" size={17} />
            <textarea
              aria-label={`Instrucción para editar el ${documentLabel} con IA`}
              disabled={aiEditLoading || !suggestionContextReady}
              maxLength={2_000}
              onChange={(event) => {
                setAiInstruction(event.target.value);
                setAiEditError("");
              }}
              onFocus={rememberAiEditSelection}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter"
                  && !event.shiftKey
                  && !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Pedile a la IA que escriba o modifique algo…"
              ref={aiInstructionTextarea}
              rows={1}
              value={aiInstruction}
            />
            <button
              aria-label={aiEditLoading
                ? "Justiniano está generando la redacción"
                : "Generar con Justiniano"}
              className="tribunalea-document-ai-command-submit"
              disabled={
                aiEditLoading
                || !suggestionContextReady
                || !aiInstruction.trim()
              }
              title={aiEditLoading ? "Generando…" : "Generar"}
              type="submit"
            >
              {aiEditLoading
                ? <LoaderCircle className="tribunalea-document-spin" size={17} />
                : <ArrowRight size={18} strokeWidth={2.4} />}
            </button>
            <span
              aria-live="polite"
              className="tribunalea-document-ai-command-status"
              role="status"
            >
              {aiEditLoading ? "Justiniano está generando la redacción." : ""}
            </span>
          </form>
          {aiEditError && (
            <p className="is-error" role="status">
              {aiEditError}
            </p>
          )}
        </div>
      )}
      {aiEditVariants.length > 0 && (
        <div
          className="tribunalea-document-ai-comparison-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) dismissAiEditComparison();
          }}
          role="presentation"
        >
          <section
            aria-labelledby="tribunalea-document-ai-comparison-title"
            aria-modal="true"
            className="tribunalea-document-ai-comparison"
            role="dialog"
          >
            <header>
              <span>
                <WandSparkles size={19} />
                <span>
                  <strong id="tribunalea-document-ai-comparison-title">
                    Compará las tres propuestas
                  </strong>
                  <small>Elegí una para aplicarla al {documentLabel}.</small>
                </span>
              </span>
              <button
                aria-label="Cerrar comparación"
                onClick={dismissAiEditComparison}
                type="button"
              >
                Descartar
              </button>
            </header>
            <div className="tribunalea-document-ai-comparison-grid">
              {aiEditVariants.map((variant) => (
                <article
                  className={`is-${variant.status || "completed"}`}
                  key={variant.model}
                >
                  <div className="tribunalea-document-ai-comparison-model">
                    <strong>{variant.label}</strong>
                    <code>{variant.model}</code>
                  </div>
                  {variant.status === "error" ? (
                    <p className="tribunalea-document-ai-comparison-error">
                      {variant.error || "Este modelo no pudo generar una propuesta."}
                    </p>
                  ) : (
                    <div className="tribunalea-document-ai-comparison-text">
                      {variant.text}
                    </div>
                  )}
                  <button
                    disabled={variant.status === "error"}
                    onClick={() => {
                      const comparison = aiEditComparison.current;
                      if (comparison) {
                        applyAiEditResult(variant, comparison);
                      }
                    }}
                    type="button"
                  >
                    Usar {variant.label}
                  </button>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
      <div className="tribunalea-document-paper">
        <EditorContent editor={editor} />
        {documentFooter && (
          <div className="tribunalea-document-footer">
            {documentFooter}
          </div>
        )}
        {suggestion && (
          <span className="tribunalea-document-suggestion-announcement" aria-live="polite">
            Sugerencia basada en {contextLabel}. Presioná Tab para aceptar o Escape para descartar.
          </span>
        )}
      </div>
    </section>
  );
}
